import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck, FileText, Download, ExternalLink, LogOut,
  ChevronRight, Loader2, Shield, Lock, Eye, Zap, Settings
} from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';
import { ProgressRing } from '../ui-custom/ProgressRing';
import { Badge } from '../ui-custom/Badge';
import { Card } from '../ui-custom/Card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = {
  security: { label: "Security", icon: Shield, color: "#FF00BE" },
  availability: { label: "Availability", icon: Zap, color: "#059669" },
  processing_integrity: { label: "Processing Integrity", icon: Settings, color: "#7c3aed" },
  confidentiality: { label: "Confidentiality", icon: Lock, color: "#d97706" },
  privacy: { label: "Privacy", icon: Eye, color: "#dc2626" },
};

function ClientPortal() {
  const navigate = useNavigate();
  const { clientUser, clientLogout } = useClientAuth();
  const [loading, setLoading] = useState(true);
  const [compliance, setCompliance] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [reports, setReports] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const token = localStorage.getItem('threebie_client_token');
    if (!token) {
      navigate('/client-login');
      return;
    }
    
    fetchPortalData(token);
  }, [navigate]);

  const fetchPortalData = async (token) => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [complianceRes, policiesRes, reportsRes] = await Promise.all([
        fetch(`${API_URL}/api/client-portal/compliance-status`, { headers }),
        fetch(`${API_URL}/api/client-portal/policies`, { headers }),
        fetch(`${API_URL}/api/client-portal/reports`, { headers }),
      ]);

      if (complianceRes.ok) {
        const data = await complianceRes.json();
        setCompliance(data);
      }

      if (policiesRes.ok) {
        const data = await policiesRes.json();
        setPolicies(data.policies || []);
      }

      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Portal fetch error:', error);
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportId, fileName) => {
    const token = localStorage.getItem('threebie_client_token');
    try {
      const response = await fetch(`${API_URL}/api/reports/${reportId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'report.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  if (loading) {
    return (
      <div className="client-portal loading">
        <Loader2 className="spin" size={40} />
        <p>Loading your compliance dashboard...</p>
      </div>
    );
  }

  return (
    <div className="client-portal">
      {/* Header */}
      <header className="portal-header">
        <div className="portal-logo">
          <ShieldCheck size={28} color="#FF00BE" />
          <span><span className="accent">Threebie</span> Client Portal</span>
        </div>
        <div className="portal-user">
          <span>{clientUser?.company_name || 'Client'}</span>
          <button onClick={clientLogout} className="logout-btn" data-testid="client-logout-btn">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="portal-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
          data-testid="tab-overview"
        >
          Overview
        </button>
        <button
          className={activeTab === 'policies' ? 'active' : ''}
          onClick={() => setActiveTab('policies')}
          data-testid="tab-policies"
        >
          Policies
        </button>
        <button
          className={activeTab === 'reports' ? 'active' : ''}
          onClick={() => setActiveTab('reports')}
          data-testid="tab-reports"
        >
          Reports
        </button>
      </nav>

      {/* Content */}
      <main className="portal-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && compliance && (
          <div className="overview-tab" data-testid="overview-content">
            <div className="welcome-banner">
              <h1>Welcome to Your Compliance Dashboard</h1>
              <p>View Threebie's SOC 2 compliance status and download documentation.</p>
            </div>

            {/* Overall Score */}
            <div className="overall-score">
              <div className="score-ring">
                <ProgressRing 
                  pct={compliance.overall_compliance} 
                  size={160} 
                  stroke={12} 
                  color={compliance.overall_compliance >= 80 ? "#059669" : "#d97706"} 
                />
                <div className="score-value">
                  {compliance.overall_compliance}%
                </div>
              </div>
              <div className="score-info">
                <h2>{compliance.framework}</h2>
                <p>Overall Compliance Score</p>
                <Badge color={compliance.overall_compliance >= 80 ? "#059669" : "#d97706"}>
                  {compliance.overall_compliance >= 90 ? 'Excellent' : 
                   compliance.overall_compliance >= 70 ? 'Good' : 'In Progress'}
                </Badge>
              </div>
            </div>

            {/* Category Breakdown */}
            <h3 className="section-title">Trust Services Categories</h3>
            <div className="categories-grid">
              {compliance.categories?.map((cat) => {
                const meta = Object.values(CATEGORIES).find(c => c.label === cat.category) || 
                             { label: cat.category, icon: Shield, color: "#6b7280" };
                const Icon = meta.icon;
                return (
                  <Card key={cat.category} className="category-card">
                    <div className="card-header">
                      <Icon size={20} color={meta.color} />
                      <span>{cat.category}</span>
                    </div>
                    <div className="card-body">
                      <div className="pct" style={{ color: meta.color }}>
                        {cat.compliance_percentage}%
                      </div>
                      <Badge color={
                        cat.status === 'Operational' ? '#059669' :
                        cat.status === 'Monitoring' ? '#d97706' : '#6b7280'
                      }>
                        {cat.status}
                      </Badge>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Quick Links */}
            <div className="quick-links">
              <Card onClick={() => setActiveTab('policies')} className="link-card">
                <FileText size={24} color="#FF00BE" />
                <div>
                  <h4>View Policies</h4>
                  <p>{policies.length} documents available</p>
                </div>
                <ChevronRight size={20} />
              </Card>
              <Card onClick={() => setActiveTab('reports')} className="link-card">
                <Download size={24} color="#059669" />
                <div>
                  <h4>Download Reports</h4>
                  <p>{reports.length} reports available</p>
                </div>
                <ChevronRight size={20} />
              </Card>
              <Card as="a" href="https://www.threebie.com/cloudinfrastructure" target="_blank" className="link-card">
                <ExternalLink size={24} color="#7c3aed" />
                <div>
                  <h4>Cloud Infrastructure</h4>
                  <p>Learn about our security</p>
                </div>
                <ChevronRight size={20} />
              </Card>
            </div>
          </div>
        )}

        {/* Policies Tab */}
        {activeTab === 'policies' && (
          <div className="policies-tab" data-testid="policies-content">
            <h2>Compliance Policies</h2>
            <p>Public policies available for your review.</p>

            <div className="policies-list">
              {policies.length === 0 ? (
                <Card className="empty-state">
                  <FileText size={40} color="#6b7280" />
                  <p>No public policies available at this time.</p>
                </Card>
              ) : (
                policies.map((policy) => (
                  <Card key={policy.policy_id} className="policy-card">
                    <div className="policy-info">
                      <h4>{policy.title}</h4>
                      <p>{policy.summary}</p>
                      <div className="policy-meta">
                        <Badge color="#6b7280">{policy.category}</Badge>
                        <span>Version {policy.version}</span>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="reports-tab" data-testid="reports-content">
            <h2>Compliance Reports</h2>
            <p>Download available compliance documentation.</p>

            <div className="reports-list">
              {reports.length === 0 ? (
                <Card className="empty-state">
                  <Download size={40} color="#6b7280" />
                  <p>No reports available at this time.</p>
                </Card>
              ) : (
                reports.map((report) => (
                  <Card key={report.report_id} className="report-card">
                    <div className="report-info">
                      <h4>{report.title}</h4>
                      <p>{report.description || `Generated ${new Date(report.generated_at).toLocaleDateString()}`}</p>
                      <Badge color="#FF00BE">{report.report_type.replace(/_/g, ' ')}</Badge>
                    </div>
                    {report.file_available && (
                      <button
                        onClick={() => downloadReport(report.report_id, `${report.title}.pdf`)}
                        className="btn-download"
                        data-testid={`download-${report.report_id}`}
                      >
                        <Download size={18} />
                        Download
                      </button>
                    )}
                  </Card>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="portal-footer">
        <p>© {new Date().getFullYear()} Threebie. All rights reserved.</p>
        <Link to="/trust-center">View Public Trust Center</Link>
      </footer>
    </div>
  );
}

export default ClientPortal;
